import { ArrowRight, BadgeCheck, Info, TrendingUp } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

const BLKBOX_REGISTER_URL = 'https://blkbox.trade/register'
const NAVION_REGISTER_URL = 'https://my.navionpro.com/register'

type LiveAccountPartnersDialogProps = {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
}

const LiveAccountPartnersDialog = ({ isOpen, onOpenChange }: LiveAccountPartnersDialogProps) => (
  <Dialog open={isOpen} onOpenChange={onOpenChange}>
    <DialogContent
      className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-5xl gap-0 overflow-y-auto rounded-2xl border !border-primary/25 !bg-[#06131d] p-0 text-white shadow-[0_24px_80px_rgba(0,0,0,0.65)]"
      overlayClassName="bg-black/70 backdrop-blur-sm"
    >
      <div className="grid lg:grid-cols-[0.9fr_1.1fr]">
        <div className="flex flex-col justify-between bg-[radial-gradient(circle_at_top_left,rgba(34,211,167,0.12),transparent_55%)] p-6 sm:p-9">
          <div>
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-primary/45 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
              <TrendingUp className="size-4" aria-hidden="true" />
              Challenge complete
            </div>
            <DialogHeader className="space-y-4 text-left">
              <DialogTitle className="text-3xl font-bold leading-tight sm:text-4xl">
                Your challenge is over.
                <span className="block text-primary">Keep trading.</span>
              </DialogTitle>
              <DialogDescription className="max-w-md text-base leading-6 text-slate-400">
                Your promotional balance has ended. Continue trading through one of our partners.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="mt-10 flex items-start gap-3 border-t border-white/10 pt-5 text-sm text-slate-400">
            <BadgeCheck className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden="true" />
            <p>Choose a partner below to create and fund your live trading account.</p>
          </div>
        </div>

        <div className="space-y-3 bg-[#081924] p-4 pt-12 sm:p-6 sm:pt-12">
          <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
            <span className="h-px flex-1 bg-white/10" />
            Continue trading with a partner
            <span className="h-px flex-1 bg-white/10" />
          </p>

          <article className="rounded-xl border border-[#f4c400]/70 bg-[#0c1117] p-4 shadow-[inset_0_0_24px_rgba(244,196,0,0.04)] sm:p-5">
            <div className="flex items-center gap-3">
              <img
                src="/assets/favicons/blkbox.png"
                alt=""
                className="size-12 rounded-lg"
                aria-hidden="true"
              />
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-xl font-bold tracking-wide">BLKBOX</h3>
                  <span className="rounded bg-[#f4c400] px-1.5 py-0.5 text-[9px] font-bold uppercase text-black">
                    Dora partner
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-400">
                  Institutional-grade platform. Global bond markets.
                </p>
              </div>
            </div>

            <div className="my-4 grid grid-cols-3 divide-x divide-white/10 border-y border-white/10 py-3 text-center text-[9px] font-semibold uppercase tracking-wide text-slate-300">
              <span>Global bond markets</span>
              <span>Institutional grade</span>
              <span>Advanced tools</span>
            </div>

            <a
              href={BLKBOX_REGISTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex min-h-11 w-full items-center justify-between rounded-md bg-[#f4c400] px-4 text-sm font-bold text-black transition-colors hover:bg-[#ffd51a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              Continue with BLKBOX
              <ArrowRight className="size-5" aria-hidden="true" />
            </a>
          </article>

          <article className="flex flex-col gap-4 rounded-xl border border-[#315578] bg-[#0a1e2d] p-4 sm:flex-row sm:items-center">
            <img
              src="/assets/images/branding/navion/logo.png"
              alt="Navion"
              className="h-8 w-auto max-w-[150px] object-contain object-left"
            />
            <p className="min-w-0 flex-1 text-xs text-slate-400">
              Professional bond trading with advanced tools.
            </p>
            <a
              href={NAVION_REGISTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-md border border-sky-500/50 px-4 text-sm font-medium text-sky-300 transition-colors hover:bg-sky-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
            >
              Continue with Navion
              <ArrowRight className="size-4" aria-hidden="true" />
            </a>
          </article>
        </div>
      </div>

      <div className="flex items-start gap-2 border-t border-white/10 bg-[#071722] px-5 py-3 text-xs text-slate-400">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <p>
          Promotional credit cannot be reactivated. Live trading is available through our partners.
        </p>
      </div>
    </DialogContent>
  </Dialog>
)

export default LiveAccountPartnersDialog
